import React, { useState, useEffect, useLayoutEffect } from 'react';
import { GeoJSON, Pane, useLeaflet } from 'react-leaflet';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import { useStoreActions, useStoreState } from 'easy-peasy';
import centroid from '@turf/centroid';

import { getMoveAngle } from '~/scripts/utils';

import Labels from './Labels';

function getAnimation(x, y) {
  return keyframes`
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(${x}px, ${y}px);
    }
  `;
}

const AnimationPane = styled(Pane)`
  animation: ${(p) => getAnimation(p.animX, p.animY)} 0.5s ease-in-out 0s;
  animation-fill-mode: forwards;

  .leaflet-interactive {
    pointer-events: ${(p) => (p.interactive ? 'auto' : 'none !important')};
  }
`;

const ChoroplethLayer = styled(GeoJSON)`
  &:hover {
    stroke: #111;
    stroke-width: 2;
  }
`;

function getPresetOffset(preset, factor, moveDir) {
  if (preset === 'bavaria' && moveDir === 1) {
    return [3 * factor, 0];
  }
  if (preset === 'eastwest' && moveDir === 1) {
    return [1.5 * factor, -0.5 * factor];
  }
  return [0, 0];
}

const GeoShape = ({ data, moveAngle, colorizer }) => {
  const { map } = useLeaflet();
  const kreisSearched = useStoreState((state) => state.app.kreisSearched);
  const kreiseGeoJson = useStoreState((state) => state.app.kreiseGeoJson);
  const isStaticTooltip = useStoreState((state) => state.tooltip.isStatic);

  const setTooltipPosition = useStoreActions(
    (state) => state.tooltip.setPosition
  );
  const setTooltipVisible = useStoreActions(
    (state) => state.tooltip.setVisible
  );
  const factor =
    typeof window !== 'undefined' ? window.innerWidth / 80 + 10 : 30;
  const setTooltipData = useStoreActions((state) => state.tooltip.setData);
  const presetOffset = getPresetOffset(data.presetName, factor, data.moveDir);

  const x = Math.cos(moveAngle) * factor * data.moveDir + presetOffset[0];
  const y = Math.sin(moveAngle) * factor * data.moveDir + presetOffset[1];

  const getStyle = (feat) => {
    const color = colorizer(feat);

    return {
      color: color,
      fillColor: color,
      opacity: 1,
      weight: 1,
      fillOpacity: 1,
    };
  };

  const onMouseEnter = (feat, layer) => (evt) => {
    layer.bringToFront();
    setTooltipVisible(true);
    setTooltipData(feat.properties);
  };

  const onMouseMove = (feat, layer) => (evt) => {
    setTooltipPosition(evt.containerPoint);
  };

  const onMouseLeave = (feat, layer) => (evt) => {
    // layer.bringToBack();
    setTooltipVisible(false);
  };

  const onEachFeature = (feat, layer) => {
    layer.on('mouseover', onMouseEnter(feat, layer));
    layer.on('mousemove', onMouseMove(feat, layer));
    layer.on('mouseout', onMouseLeave(feat, layer));
  };

  useEffect(() => {
    const searchedFeature = kreiseGeoJson.features.find(
      (f) => f.properties.Wahlgebiet_ID === kreisSearched
    );
    if (searchedFeature) {
      setTooltipVisible(true);
      setTooltipData(searchedFeature?.properties);

      const searchedCentroid = centroid(searchedFeature);
      const pixelCoord = map.latLngToContainerPoint([
        searchedCentroid.geometry.coordinates[1],
        searchedCentroid.geometry.coordinates[0],
      ]);
      // const layerPoint = map.latLngToLayerPoint(latlon);
      setTooltipPosition(pixelCoord);
    } else {
      setTooltipVisible(false);
    }
  }, [kreisSearched]);

  const isInteractive = !isStaticTooltip;

  return (
    <AnimationPane animX={x} animY={y} interactive={isInteractive}>
      <GeoJSON
        data={data.bufferedOutline}
        color="#AAA"
        fillColor="#FFF"
        fillOpacity={1}
        weight={1}
      />
      <ChoroplethLayer
        data={data.geojson}
        style={getStyle}
        weight={1}
        onEachFeature={onEachFeature}
      />
      <Labels data={data.labels} />
    </AnimationPane>
  );
};

const Choropleth = ({ data, initialData }) => {
  const { map } = useLeaflet();
  const colorizer = useStoreState((state) => state.app.mapColorizer);

  if (!data) {
    return (
      <GeoJSON
        data={initialData}
        color="#ddd"
        fillColor="#eee"
        fillOpacity={1}
        weight={1}
        opacity={1}
      />
    );
  }

  const moveAngle = getMoveAngle(data.shapes, map);

  return data.shapes
    .slice(0, 2)
    .map((shape, i) => (
      <GeoShape
        data={shape}
        colorizer={colorizer}
        moveAngle={moveAngle}
        key={`${data.id}__${i}`}
      />
    ));
};

export default Choropleth;
